import React, { useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow
} from '@material-ui/core';
import Close from '@material-ui/icons/Close';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { closeEntityChangelogs, fetchEntityChangelogs } from '../../reducers/entityChangelogs';
import LoadingIndicator from '../LoadingIndicator';
import { formatTimestamp } from '../../helpers/time';
import Changes from './Changes';

const AttributeChanges = ({ changes, tab }) => {
  return (
    <div style={{ marginLeft: tab * 8 }}>
      {Object.entries(changes).map(([key, change]) => {
        const capitalize = str =>
          str
            .toLowerCase()
            .replace(/_/g, ' ')
            .replace(/\b\w/g, char => char.toUpperCase());

        const attributeLabel = capitalize(change.custom_label ?? key);
        const oldValueLabel = change.old_formatted ? change.old_formatted : change.old;
        const newValueLabel = change.new_formatted ? change.new_formatted : change.new;
        return (
          <div key={key}>
            <strong>{attributeLabel}</strong>: <i>{oldValueLabel}</i> → <i>{newValueLabel}</i>
          </div>
        );
      })}
    </div>
  );
};

AttributeChanges.propTypes = {
  changes: PropTypes.objectOf(
    PropTypes.shape({
      custom_label: PropTypes.string,
      old_formatted: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      old: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      new_formatted: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      new: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    })
  ).isRequired,
  tab: PropTypes.number
};

AttributeChanges.defaultProps = {
  tab: 0
};

const RelationalItem = ({ item }) => {
  if (typeof item === 'string') {
    return <i>{item}</i>;
  }

  return (
    <div style={{ marginLeft: 8 }}>
      {Object.keys(item).map(key => {
        return (
          <div key={key}>
            <strong>{key}</strong>:&nbsp;
            {item[key]}
          </div>
        );
      })}
    </div>
  );
};

const ChangelogItem = ({ item }) => {
  return (
    <>
      {!!item.changes && <AttributeChanges changes={item.changes} />}
      {!!item?.relational_changes &&
        !!item?.relational_changes?.length &&
        item.relational_changes.map(relationalChange => {
          return (
            <>
              {!!relationalChange.added && (
                <div key={`${item.id}-${relationalChange.name}-added`}>
                  <strong>Added {relationalChange.name}:</strong>&nbsp;
                  {relationalChange.added.map(addedItem => (
                    <RelationalItem item={addedItem} />
                  ))}
                </div>
              )}
              {!!relationalChange.removed && (
                <div key={`${item.id}-${relationalChange.name}-removed`}>
                  <strong>Removed {relationalChange.name}:</strong>&nbsp;
                  {relationalChange.removed.map(removedItem => (
                    <RelationalItem item={removedItem} />
                  ))}
                </div>
              )}
              {!!relationalChange.message && (
                <div key={`${item.id}-${relationalChange.name}-message`}>
                  <i>{relationalChange.message}</i>
                </div>
              )}
              {!!relationalChange.changes && Array.isArray(relationalChange.changes) && (
                <div>
                  <strong>{relationalChange.name} Changes:</strong>
                  {relationalChange.changes.map(change => (
                    <>
                      <AttributeChanges changes={change} tab={1} />
                      <Divider />
                    </>
                  ))}
                </div>
              )}
              {!!relationalChange.changes && !Array.isArray(relationalChange.changes) && (
                <div>
                  <strong>{relationalChange.name}</strong>
                  <AttributeChanges changes={relationalChange.changes} tab={1} />
                </div>
              )}
            </>
          );
        })}
    </>
  );
};

ChangelogItem.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.number.isRequired,
    changes: PropTypes.objectOf(
      PropTypes.shape({
        custom_label: PropTypes.string,
        old_formatted: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        old: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        new_formatted: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        new: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      })
    ),
    relational_changes: PropTypes.arrayOf(
      PropTypes.shape({
        added: PropTypes.arrayOf(PropTypes.string),
        removed: PropTypes.arrayOf(PropTypes.string),
        name: PropTypes.string.isRequired,
        message: PropTypes.string,
        changes: PropTypes.objectOf(
          PropTypes.shape({
            custom_label: PropTypes.string,
            old_formatted: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            old: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            new_formatted: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            new: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
          })
        )
      })
    ),
    reason: PropTypes.string,
    author_name: PropTypes.string,
    logged_at: PropTypes.string.isRequired
  }).isRequired
};

const Changelog = ({
  entityId,
  label,
  items,
  total,
  fetch,
  fetching,
  fetched,
  params: { perPage, page },
  close
}) => {
  useEffect(() => {
    if (entityId && !fetched && !fetching) {
      fetch({ page, perPage });
    }
  }, [entityId, fetch, fetching, fetched, perPage, page]);

  const handleChangePage = (event, newPage) => {
    fetch({ page: newPage + 1, perPage });
  };

  const handleChangeRowsPerPage = event => {
    fetch({ page: 1, perPage: parseInt(event.target.value, 10) });
  };

  if (!entityId) {
    return '';
  }

  return (
    <Dialog open onClose={close} maxWidth="md" fullWidth>
      <DialogTitle>
        Changelog for {label}
        <IconButton
          aria-label="close"
          onClick={close}
          style={{ position: 'absolute', right: 8, top: 8 }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent style={{ paddingBottom: 24 }}>
        {fetching && <LoadingIndicator />}
        {fetched && items.length === 0 && (
          <div style={{ textAlign: 'center' }}>No changes found.</div>
        )}
        {fetched && items.length > 0 && (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Author</TableCell>
                  <TableCell>Changes</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Timestamp</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map(item => {
                  return (
                    <TableRow key={`changelog-${item.id}`}>
                      <TableCell>{item.author_name}</TableCell>
                      <TableCell>
                        <Changes version={item.changes.version} data={item.changes.data} />
                      </TableCell>
                      <TableCell>{item.reason}</TableCell>
                      <TableCell>{formatTimestamp(item.logged_at)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={total}
              rowsPerPage={perPage}
              page={page - 1}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TableContainer>
        )}
      </DialogContent>
    </Dialog>
  );
};

Changelog.defaultProps = {
  entityId: null,
  label: '',
  items: []
};

Changelog.propTypes = {
  entityId: PropTypes.number,
  label: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      author_name: PropTypes.string.isRequired,
      changes: PropTypes.objectOf(
        PropTypes.shape({
          custom_label: PropTypes.string,
          old_formatted: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          old: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          new_formatted: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          new: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        })
      ).isRequired,
      reason: PropTypes.string,
      logged_at: PropTypes.string.isRequired
    })
  ),
  total: PropTypes.number.isRequired,
  fetch: PropTypes.func.isRequired,
  fetching: PropTypes.bool.isRequired,
  fetched: PropTypes.bool.isRequired,
  params: PropTypes.shape({
    perPage: PropTypes.number.isRequired,
    page: PropTypes.number.isRequired
  }).isRequired,
  close: PropTypes.func.isRequired
};

export default connect(
  state => {
    const changelogState = state.entityChangelogs;
    return {
      entityId: changelogState.entityId,
      entityName: changelogState.entityName,
      label: changelogState.entityLabel,
      items: changelogState.items,
      total: changelogState.total,
      fetching: changelogState.fetching,
      fetched: changelogState.fetched,
      params: changelogState.params
    };
  },
  dispatch => {
    return {
      fetch: params => dispatch(fetchEntityChangelogs(params)),
      close: () => dispatch(closeEntityChangelogs())
    };
  },
  (stateProps, dispatchProps, ownProps) => {
    return {
      ...stateProps,
      ...dispatchProps,
      ...ownProps,
      fetch: params =>
        dispatchProps.fetch({
          entityId: stateProps.entityId,
          entityName: stateProps.entityName,
          ...params
        })
    };
  }
)(Changelog);
